import React, { useState, useEffect, useRef, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Skeleton, { SkeletonTheme } from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { createCleanMarkdownComponents } from '../ChatMessage/utils/markdownHelpers';
import { filterThinkingContent } from '../../utils/thinkingFilter';

const ThinkingIndicator = ({ 
  isVisible = true,
  isThinking = false,
  thinkingContent = '',
  duration = 0,
  onComplete 
}) => {
  const [isExpanded, setIsExpanded] = useState(false); // Start collapsed by default
  const [displayedContent, setDisplayedContent] = useState('');
  const [showTopGradient, setShowTopGradient] = useState(false);
  const [showBottomGradient, setShowBottomGradient] = useState(false);
  const wasThinkingRef = useRef(false);
  const hasCollapsedRef = useRef(false);
  const contentRef = useRef(null);

  // Create markdown components
  const markdownComponents = useMemo(() => 
    createCleanMarkdownComponents(document.documentElement.classList.contains('dark'), 0), 
    []
  );

  // Check if the content can scroll and update gradient visibility
  const checkScrollable = () => {
    const element = contentRef.current;
    if (!element) return;
    
    const canScroll = element.scrollHeight > element.clientHeight;
    const isAtTop = element.scrollTop <= 5; // 5px threshold
    const isAtBottom = element.scrollTop + element.clientHeight >= element.scrollHeight - 5; // 5px threshold
    
    setShowTopGradient(canScroll && !isAtTop);
    setShowBottomGradient(canScroll && !isAtBottom);
  };

  // Auto-expand/collapse based on thinking state
  useEffect(() => {
    // Expand when thinking starts (reset collapse flag)
    if (isThinking && !wasThinkingRef.current) {
      setIsExpanded(true);
      hasCollapsedRef.current = false;
    }
    
    // Only trigger collapse when transitioning from thinking to not thinking, and haven't collapsed yet
    if (wasThinkingRef.current && !isThinking && thinkingContent && duration > 0 && !hasCollapsedRef.current) {
      const timer = setTimeout(() => {
        setIsExpanded(false);
        hasCollapsedRef.current = true;
        if (onComplete) {
          onComplete();
        }
      }, 1000); // Wait 1 second before collapsing
      
      return () => clearTimeout(timer);
    }
    
    // Update the ref to track thinking state
    wasThinkingRef.current = isThinking;
  }, [isThinking, thinkingContent, duration, onComplete]);

  // Update displayed content when thinking content changes
  useEffect(() => {
    // Filter the thinking content to remove problematic internal commentary
    const filteredContent = filterThinkingContent(thinkingContent);
    setDisplayedContent(filteredContent);
  }, [thinkingContent]);

  // Effect to check scrollable state when content changes or expands
  useEffect(() => {
    if ((isExpanded || isThinking) && displayedContent) {
      // Small delay to ensure DOM is updated
      setTimeout(checkScrollable, 100);
    }
  }, [isExpanded, isThinking, displayedContent]);

  const toggleExpanded = () => {
    setIsExpanded(!isExpanded);
  };

  if (!isVisible) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -10 }}
        transition={{ duration: 0.3 }}
        className="mb-3 max-[1200px]:mb-2.5 max-[900px]:mb-2 [@media(max-height:800px)]:mb-2.5 [@media(max-height:700px)]:mb-2"
      >
        {/* Header - always outside container to prevent layout shift */}
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: 'auto' }}
          className="flex items-center gap-2 pt-0.5 cursor-pointer transition-colors max-[1200px]:gap-1.5 max-[900px]:gap-1 [@media(max-height:800px)]:gap-1.5 [@media(max-height:700px)]:gap-1"
          onClick={toggleExpanded}
        >
          <div className="flex items-center">
            {isThinking ? (
              <SkeletonTheme 
                baseColor={document.documentElement.classList.contains('dark') ? "#999999" : "#9ca3af"} 
                highlightColor={document.documentElement.classList.contains('dark') ? "#bbbbbb" : "#d1d5db"}
              >
                <div 
                  style={{
                    width: '80px',
                    height: '16px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'flex-start'
                  }}
                >
                  <div
                    style={{
                      width: '80px',
                      height: '16px',
                      maskImage: `url("data:image/svg+xml,${encodeURIComponent(`
                        <svg width="80" height="16" viewBox="0 0 80 16" xmlns="http://www.w3.org/2000/svg">
                          <text x="0" y="12" font-family="system-ui, -apple-system, sans-serif" font-size="14" fill="black">Thinking</text>
                        </svg>
                      `)}")`,
                      WebkitMaskImage: `url("data:image/svg+xml,${encodeURIComponent(`
                        <svg width="80" height="16" viewBox="0 0 80 16" xmlns="http://www.w3.org/2000/svg">
                          <text x="0" y="12" font-family="system-ui, -apple-system, sans-serif" font-size="14" fill="black">Thinking</text>
                        </svg>
                      `)}")`,
                      maskSize: 'contain',
                      WebkitMaskSize: 'contain',
                      maskRepeat: 'no-repeat',
                      WebkitMaskRepeat: 'no-repeat',
                      maskPosition: 'left center',
                      WebkitMaskPosition: 'left center'
                    }}
                  >
                    <div style={{ width: '100%', height: '100%' }}>
                      <Skeleton 
                        width={80}
                        height={16}
                        style={{ 
                          borderRadius: 0,
                          display: 'block',
                          margin: 0,
                          padding: 0,
                          lineHeight: '1',
                          verticalAlign: 'top'
                        }}
                      />
                    </div>
                  </div>
                </div>
              </SkeletonTheme>
            ) : (
              <span className="text-[14px] text-gray-600 dark:text-[#999999] max-[1200px]:text-[12px] max-[900px]:text-[11px] [@media(max-height:800px)]:text-[12px] [@media(max-height:700px)]:text-[11px]">
                Thought for {duration.toFixed(0)} {duration.toFixed(0) === '1' ? 'second' : 'seconds'}
              </span>
            )}
          </div>
          {!isThinking && (
            <motion.div
              animate={{ rotate: isExpanded ? 180 : 0 }}
              transition={{ duration: 0.2 }}
            >
              <svg className="w-4 h-4 text-gray-600 dark:text-[#999999] max-[1200px]:w-3.5 max-[1200px]:h-3.5 max-[900px]:w-3 max-[900px]:h-3 [@media(max-height:800px)]:w-3.5 [@media(max-height:800px)]:h-3.5 [@media(max-height:700px)]:w-3 [@media(max-height:700px)]:h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </motion.div>
          )}
        </motion.div>

        {/* Container - only appears when expanded/thinking */}
        {(isExpanded || isThinking) && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="thinking-container relative overflow-hidden bg-white dark:bg-[#111111] mt-4 max-[1200px]:mt-3 max-[900px]:mt-2.5 [@media(max-height:800px)]:mt-3 [@media(max-height:700px)]:mt-2.5"
          >
            {/* Top gradient overlay - only shows when can scroll up */}
            {showTopGradient && (
              <div 
                className="absolute top-0 left-0 right-0 h-10 pointer-events-none z-10"
                style={{
                  background: document.documentElement.classList.contains('dark') 
                    ? 'linear-gradient(to top, transparent, #111111)'
                    : 'linear-gradient(to top, transparent, #ffffff)'
                }}
              />
            )}

            {/* Thinking content */}
            <div 
              ref={contentRef}
              className="px-3 max-h-64 overflow-y-auto max-[1200px]:px-2.5 max-[900px]:px-2 max-[1200px]:max-h-56 max-[900px]:max-h-48 [@media(max-height:800px)]:px-2.5 [@media(max-height:800px)]:max-h-56 [@media(max-height:700px)]:px-2 [@media(max-height:700px)]:max-h-48"
              onScroll={checkScrollable}
            >
              <div className="text-xs text-gray-600 dark:text-[#999999] leading-relaxed prose prose-sm dark:prose-invert max-w-none prose-p:mb-2 prose-headings:mb-2 prose-headings:mt-0 prose-li:mb-0 max-[1200px]:text-[11px] max-[900px]:text-[10px] [@media(max-height:800px)]:text-[11px] [@media(max-height:700px)]:text-[10px]">
                {displayedContent ? (
                  <ReactMarkdown 
                    remarkPlugins={[remarkGfm]}
                    components={markdownComponents}
                  >
                    {displayedContent}
                  </ReactMarkdown>
                ) : (
                  <span>{isThinking ? 'Starting to think...' : 'No thinking content available'}</span>
                )}
                {isThinking && (
                  <motion.span
                    animate={{ opacity: [1, 0] }}
                    transition={{ duration: 0.8, repeat: Infinity }}
                    className="inline-block ml-1"
                  >
                    ▊
                  </motion.span>
                )}
              </div>
            </div>

            {/* Bottom gradient overlay - only shows when can scroll down */}
            {showBottomGradient && (
              <div 
                className="absolute bottom-0 left-0 right-0 h-10 pointer-events-none z-10"
                style={{
                  background: document.documentElement.classList.contains('dark') 
                    ? 'linear-gradient(to bottom, transparent, #111111)'
                    : 'linear-gradient(to bottom, transparent, #ffffff)'
                }}
              />
            )}

            {/* Subtle animation overlay for active thinking */}
            {isThinking && (
              <motion.div
                animate={{ opacity: [0.02, 0.05, 0.02] }}
                transition={{ duration: 2, repeat: Infinity }}
                className="absolute inset-0 rounded-xl bg-[#f5f5f5] dark:bg-[#1a1a1a] pointer-events-none"
              />
            )}
          </motion.div>
        )}
      </motion.div>
    </AnimatePresence>
  );
};

export default ThinkingIndicator; 